import { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, patch, post } from "@/lib/api";
import { EMAIL_RE, parseList, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { FieldError, PageHeader } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { email: "", full_name: "", phone: "", areas_covered: "", postcodes: "", active: true, open_jobs_enabled: false, role: "locksmith", user_types: ["locksmith"] };

export const LocksmithDialog = ({ open, onOpenChange, locksmith, initialRole, onSaved, onInvited, canInviteManager, pageMode = false }) => {
  const isEdit = !!locksmith;
  const { profile } = useAuth();
  const enabledTypes = profile.company?.enabled_user_types || WORKER_ROLE_OPTIONS.map(([role]) => role);
  const workerOptions = WORKER_ROLE_OPTIONS.filter(([role]) => enabledTypes.includes(role));
  const defaultRole = workerOptions.some(([role]) => role === initialRole) ? initialRole : workerOptions[0]?.[0] || "locksmith";
  const emailLocked = isEdit && (locksmith.id === profile.id || !!locksmith.other_companies);
  const [f, setF] = useState(blank);
  const memberTypeOptions = WORKER_ROLE_OPTIONS.filter(([role]) => !(profile.company?.sitewide_disabled_types || []).includes(role) && (enabledTypes.includes(role) || (isEdit && f.user_types?.includes(role))));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF(locksmith ? { ...blank, ...locksmith, user_types: locksmith.user_types || (WORKER_ROLE_OPTIONS.some(([role]) => role === locksmith.role) ? [locksmith.role] : []), phone: locksmith.phone || "", areas_covered: locksmith.areas_covered || "", postcodes: (locksmith.postcodes || []).join(", ") } : { ...blank, role: defaultRole, user_types: [defaultRole] });
      setErrors({});
    }
  }, [open, locksmith, defaultRole]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!emailLocked && !EMAIL_RE.test(f.email.trim())) errs.email = "Enter a valid email address";
    if (f.full_name.trim().length < 2) errs.full_name = "Enter their name";
    if ((!isEdit || f.role !== "manager") && !f.user_types.length) errs.user_types = "Select at least one user type";
    const pcs = parseList(f.postcodes);
    if (pcs.some((p) => !/^[A-Z0-9 ]{1,8}$/.test(p))) errs.postcodes = "Use postcode districts like E13, IG11, separated by commas";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const data = { full_name: f.full_name.trim(), phone: f.phone.trim() || null, areas_covered: f.areas_covered.trim() || null, postcodes: pcs };
    try {
      if (isEdit) {
        const newEmail = f.email.trim().toLowerCase();
        await patch(`/locksmiths/${locksmith.id}`, { ...data, user_types: f.user_types, active: f.active, open_jobs_enabled: !!f.open_jobs_enabled, ...(!emailLocked && newEmail !== locksmith.email ? { email: newEmail } : {}) });
        toast.success(!emailLocked && newEmail !== locksmith.email ? `Saved. ${data.full_name} now signs in with ${newEmail}` : "Locksmith updated");
      } else {
        const res = await post("/locksmiths/invite", { ...data, email: f.email.trim().toLowerCase(), role: f.role, user_types: f.role === "manager" ? [] : f.user_types });
        toast.success(res.existing_account ? `${data.full_name} already has a Warrant Whizz account. We've emailed them your invite to accept when they sign in.` : `${data.full_name} invited. We've emailed them their set-password link.`);
        if (res.invite_link) onInvited({ name: data.full_name, link: res.invite_link, locksmith: res.locksmith });
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const input = (key, label, props = {}) => (
    <div>
      <Label htmlFor={`ls-${key}`}>{label}</Label>
      <Input id={`ls-${key}`} value={f[key]} onChange={(e) => { setF({ ...f, [key]: e.target.value }); if (errors[key]) setErrors({ ...errors, [key]: undefined }); }} className="mt-1.5" data-testid={`locksmith-${key}-input`} {...props} />
      <FieldError message={errors[key]} testId={`locksmith-${key}-error`} />
    </div>
  );

  const form = (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid={pageMode ? "team-member-edit-form" : undefined}>
          {!emailLocked && input("email", "Email *", { type: "email" })}
          {isEdit && !emailLocked && f.email.trim().toLowerCase() !== locksmith.email && <p className="-mt-2 text-xs text-amber-700" data-testid="locksmith-email-change-note">They&apos;ll sign in with the new email straight away. We&apos;ll email both addresses to let them know.</p>}
          {isEdit && emailLocked && <p className="text-xs text-slate-500" data-testid="locksmith-email-locked-note">{locksmith.id === profile.id ? "Change your own email from your Profile page." : "This person also works for another company, so only they can change their email."}</p>}
          {!isEdit && (
            <div className="space-y-3">
              <fieldset>
                <legend className="mb-2 text-sm font-semibold text-slate-900">User types</legend>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {workerOptions.map(([type, label]) => (
                    <label key={type} className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm" data-testid={`invite-role-${type}`}>
                      <Checkbox checked={f.user_types.includes(type)} onCheckedChange={(checked) => {
                        const user_types = checked ? [...f.user_types, type] : f.user_types.filter((value) => value !== type);
                        const role = f.role === "manager" || user_types.includes(f.role) ? (f.role === "manager" ? type : f.role) : user_types[0] || "";
                        setF({ ...f, user_types, role });
                      }} />
                      {label}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.user_types} testId="invite-user-types-error" />
              </fieldset>
              {canInviteManager && <Button type="button" variant={f.role === "manager" ? "default" : "outline"} onClick={() => setF({ ...f, role: "manager", user_types: [] })} data-testid="invite-role-manager">
                {f.role === "manager" ? "Manager selected" : "Invite as Manager"}
              </Button>}
            </div>
          )}
          {isEdit && f.role !== "manager" && (
            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-slate-900">User types</legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {memberTypeOptions.map(([type, label]) => (
                  <label key={type} className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm">
                    <Checkbox checked={f.user_types.includes(type)} onCheckedChange={(checked) => setF({ ...f, user_types: checked ? [...f.user_types, type] : f.user_types.filter((value) => value !== type) })} />
                    {label}
                  </label>
                ))}
              </div>
              <FieldError message={errors.user_types} testId="edit-user-types-error" />
            </fieldset>
          )}
          {!isEdit && !workerOptions.length && !canInviteManager && <p className="text-sm text-slate-600">No worker types are enabled. Ask the company owner to enable one in Company settings.</p>}
          {input("full_name", "Full name *")}
          {input("phone", "Phone", { type: "tel" })}
          <div>
            <Label htmlFor="ls-areas">Areas covered</Label>
            <Textarea id="ls-areas" rows={2} value={f.areas_covered} onChange={(e) => setF({ ...f, areas_covered: e.target.value })} className="mt-1.5" placeholder="e.g. Newham, Barking, Ilford" data-testid="locksmith-areas-input" />
          </div>
          {input("postcodes", "Postcodes covered", { placeholder: "E6, E13, IG11", className: "font-data mt-1.5 uppercase" })}
          {isEdit && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <Label htmlFor="ls-active">Active account</Label>
              <Switch id="ls-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="locksmith-active-switch" />
            </div>
          )}
          {isEdit && WORKER_ROLE_OPTIONS.some(([role]) => role === locksmith.role) && (
            <label htmlFor="ls-open-jobs" className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3">
              <Checkbox id="ls-open-jobs" checked={!!f.open_jobs_enabled} onCheckedChange={(v) => setF({ ...f, open_jobs_enabled: v === true })} className="mt-0.5" data-testid="locksmith-open-jobs-checkbox" />
              <span className="text-sm"><span className="block font-medium text-slate-900">Show Available Jobs tab</span><span className="block text-xs text-slate-500">Lets them see and accept open runs, and get open job emails</span></span>
            </label>
          )}
          {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="locksmith-form-error">{errors.form}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="locksmith-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy || (!isEdit && !workerOptions.length && !canInviteManager)} className="bg-blue-700 hover:bg-blue-800" data-testid="locksmith-save-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isEdit ? "Save" : "Create invite"}
            </Button>
          </DialogFooter>
    </form>
  );

  if (pageMode) return (
    <div className="space-y-6" data-testid="team-member-edit-page">
      <PageHeader eyebrow="Team" title="Edit team member" description={locksmith.full_name || locksmith.email}
        actions={<Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="team-member-edit-back-button"><ArrowLeft className="mr-1 h-4 w-4" />Back to team</Button>} />
      <div className="max-w-3xl rounded-xl border border-slate-200 bg-white p-5 shadow-sm">{form}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg" data-testid="locksmith-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit team member" : "Invite to your team"}</DialogTitle>
          <DialogDescription>{isEdit ? locksmith.email : "You'll get a private set-password link to send them. Accounts are invite only."}</DialogDescription>
        </DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
};
